import React from "react";
import {Box, InputBase, Typography} from "@mui/material";
import {ResizeGripper, useResizablePanel} from "../Common/ResizablePanel";
import {ObjectStatistics, ObjectTypeRow, OBJECT_STATUSES} from "../../util/objectStatistics";

// An object list's left pane: "Filter By" over the lifecycle statuses and how recently a record was
// created. Visual language is lifted from the community Answers left pane (ParkourAnswers.tsx) so the
// two read as the same product: 11px rows, square checkboxes, right-aligned counts, and section
// headers ruled off with a bottom border. Newest and the Days-old row are modelled on that pane's
// recency filters, down to the checkbox-plus-number-input layout.

const TEXT = "#2B2B2B";
const HEADING = "#000000";
const MUTED = "#717171";
const LINK = "#1498D8";
const BORDER = "#E0E0E0";

const fmt = (n: number) => n.toLocaleString();

type Props = {
    /** Selected Status values (the stored form, e.g. "RELEASED"), not the labels. */
    selectedStatuses: Set<string>;
    /** The rows to offer. Defaults to the nine classifications' shared lifecycle. A CHANGE ORDER
     *  has its own five-value lane, so it passes its own -- one pane, two vocabularies, rather than
     *  a second component that drifts from this one visually the first time either is touched. */
    statuses?: {label: string; status: string}[];
    /** Heading over the status rows. "Status" for a record, "State" for a change order, because
     *  those are the words each screen already uses for the column. */
    statusHeading?: string;
    /** Recency has no meaning on a list whose rows are all a few days old and whose useful axis is
     *  lifecycle; a change order list hides it rather than showing two controls nobody moves. */
    showRecency?: boolean;
    /** Width is remembered per pane. Sharing one key would make resizing the record list resize the
     *  change order list, which reads as the panel moving on its own. */
    storageKey?: string;
    onToggleStatus: (status: string) => void;
    /** Created within the last week. */
    newestOnly: boolean;
    onToggleNewest: () => void;
    /** Created within the last `daysOld` days. */
    daysOldEnabled: boolean;
    onToggleDaysOld: () => void;
    /** Held as typed, so a half-entered "" or "0" reaches here; the caller decides what is usable. */
    daysOld: string;
    onDaysOldChange: (value: string) => void;
    /** Selected OBJECT_TYPE acronyms. Optional, and absent on the change order list, which has no
     *  types -- the section is driven by the statistics, so a list with none never shows it. */
    selectedTypes?: Set<string>;
    onToggleType?: (acronym: string) => void;
    /** Heading over the type rows. "Type" reads right on every classification this ships. */
    typeHeading?: string;
    /** The form questions this list can be filtered by, each with its answers and their counts.
     *
     *  A FIFTH SECTION, NOT A REWRITE. The four above are bespoke for good reasons the comments
     *  beside them give -- a type filters by acronym, recency by a date column, status by a stored
     *  value -- and flattening them into one generic model would lose exactly that. Facets ARE
     *  generic, because every one is the same shape: a question, its answers, a count each. So they
     *  get the generic treatment and the others keep theirs.
     *
     *  Absent on a list whose forms mark nothing filterable, which is every list today. */
    facets?: Facet[];
    /** How many filterable questions the forms in play declare, and whether the pane was cut to
     *  fit. A pane that stops without saying so tells a reader the thirteenth question cannot be
     *  filtered, which is a different statement and a false one. */
    facetsAvailable?: number;
    facetsTruncated?: boolean;
    /** Ticked answers, keyed by field code. A field with an empty set is not filtering. */
    selectedFacets?: Record<string, Set<string>>;
    onToggleFacet?: (code: string, value: string) => void;
    /** Typed bounds for the range facets, keyed by field code. Held as typed -- a half-entered
     *  "12" or an empty string reaches here, and the EC decides what is usable -- for the same
     *  reason `daysOld` is a string. */
    facetRanges?: Record<string, {from: string; to: string}>;
    onFacetRangeChange?: (code: string, bound: "from" | "to", value: string) => void;
    statistics: ObjectStatistics | null;
    onClear: () => void;
};

export type Facet = {
    Code: string;
    Label: string;
    /** "values" for a question with answers to tick, "range" for one with two bounds to type.
     *
     *  TWO SHAPES BECAUSE THERE ARE TWO QUESTIONS. A Select has a handful of answers and a count
     *  each, so the useful thing to show is the list. A Number has as many answers as there are
     *  records and no two the same, so a list of them is not a filter -- the useful thing to show
     *  is the span they cover and let the reader narrow it. Absent on an older payload, which is
     *  read as "values". */
    Kind?: "values" | "range";
    /** Which control declared it, for a range: the server maps that onto a typed column. */
    Control?: string;
    /** The span the answers actually cover, so the reader types inside it rather than guessing. */
    Min?: string | number | null;
    Max?: string | number | null;
    /** How many records answer this question at all. */
    Answered?: number;
    Values: {Value: string; Count: number}[];
    /** How many distinct answers exist, which can exceed what Values carries. */
    DistinctValues?: number;
    /** True when the server cut the list. Said rather than silently shown short. */
    Truncated?: boolean;
};

/** The types in tree order, each with its depth. Roots alphabetical, children under their parent.
 *
 *  AN ORPHAN IS A ROOT. A type whose parent is not in the list -- filtered out as inactive, say --
 *  would otherwise never be drawn at all, which is a worse answer than drawing it at the top.
 *  Cycle-safe by the visited set: the server refuses to write one, and a pane that hung because a
 *  cycle existed would be a bad way to find that out. */
function nestTypes(types: ObjectTypeRow[]): {type: ObjectTypeRow; depth: number}[] {
    const present = new Set(types.map(one => one.usid));
    const byParent = new Map<string, ObjectTypeRow[]>();
    for (const type of types) {
        const key = type.parentUSID && present.has(type.parentUSID) ? type.parentUSID : "";
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key)!.push(type);
    }
    for (const list of byParent.values()) list.sort((a, b) => a.acronym.localeCompare(b.acronym));

    const out: {type: ObjectTypeRow; depth: number}[] = [];
    const seen = new Set<string>();
    const walk = (parent: string, depth: number) => {
        for (const type of byParent.get(parent) ?? []) {
            if (seen.has(type.usid)) continue;
            seen.add(type.usid);
            out.push({type, depth});
            walk(type.usid, depth + 1);
        }
    };
    walk("", 0);
    return out;
}

function Checkbox({checked}: {checked: boolean}) {
    return (
        <Box sx={{
            width: 13, height: 13, flexShrink: 0, borderRadius: "2px",
            border: `1px solid ${checked ? LINK : "#B5B5B5"}`,
            backgroundColor: checked ? LINK : "#FFFFFF",
            display: "flex", alignItems: "center", justifyContent: "center",
        }}>
            {checked && <Box sx={{width: 6, height: 6, backgroundColor: "#FFFFFF", borderRadius: "1px"}}/>}
        </Box>
    );
}

export function FilterRow({label, title, count, checked, indent = 0, onToggle}: {label: string; title?: string; count?: number; checked: boolean; indent?: number; onToggle: () => void}) {
    return (
        // THE TITLE CARRIES WHAT THE LABEL CANNOT. An acronym row reads "SOP"; the type is called
        // "Standard Operating Procedure", which does not fit a 240px pane and is the thing a reader
        // who does not already know the acronym needs. Hovering is not discoverable, but a truncated
        // name in the row would be worse than a short one that is exact.
        <Box onClick={onToggle} title={title}
             sx={{display: "flex", alignItems: "center", gap: 0.75, cursor: "pointer", py: "2px",
                  pl: `${indent * 12}px`, "&:hover .lbl": {textDecoration: "underline"}}}>
            <Checkbox checked={checked}/>
            <Typography className="lbl" sx={{
                flex: 1, fontSize: 11, lineHeight: 1.5,
                color: checked ? LINK : TEXT, fontWeight: checked ? 700 : 400,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
                {label}
            </Typography>
            {/* An undefined count renders nothing; a zero renders "0". Collapsing the two would
                hide the useful fact that a status genuinely has no documents. */}
            {count !== undefined && <Typography sx={{fontSize: 10, color: MUTED, ml: 1}}>{fmt(count)}</Typography>}
        </Box>
    );
}

/** The span a range facet covers, written the way its control is read.
 *
 *  A DATETIME TRIMMED TO A DATE. The projection stores a timestamp, so the bounds arrive as
 *  "2026-02-14 09:00:00" -- which a date input will not accept as a value and a reader does not
 *  want to see for a field they answered with a day. */
function bound(facet: Facet, which: "Min" | "Max"): string {
    const raw = facet[which];
    if (raw === null || raw === undefined) return "";
    return facet.Control === "Date" ? String(raw).slice(0, 10) : String(raw);
}

/** Two bounds over one form question, either of which may be left blank.
 *
 *  THE SPAN IS THE FACET. A value facet earns its place by showing the answers and their counts;
 *  a range has no such list -- every record answers 4.25 or 58.65 or something in between -- so
 *  what it owes the reader is the span those answers cover and how many records answer at all.
 *  Without it the two empty boxes are a guess: type 100 to 200 over a field whose answers run
 *  250 to 5000 and the list goes empty with nothing on screen explaining why.
 *
 *  NEITHER BOUND IS REQUIRED. "Under 1000" and "over 4000" are the two most useful questions
 *  here, and both are half a range. Nothing is filtered until one is typed. */
export function RangeRow({facet, from, to, onChange}: {
    facet: Facet; from: string; to: string;
    onChange: (bound: "from" | "to", value: string) => void;
}) {
    const kind = facet.Control === "Date" ? "date" : "number";
    const active = from !== "" || to !== "";
    const box = {
        flex: 1, minWidth: 0, fontSize: 11,
        border: `1px solid ${active ? LINK : BORDER}`, borderRadius: "2px",
        "& input": {p: "1px 4px", textAlign: "center" as const},
    };
    return (
        <Box sx={{pl: "19px", pr: "4px", pb: "2px"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                <InputBase type={kind} value={from} onChange={e => onChange("from", e.target.value)}
                           inputProps={{"aria-label": `${facet.Label} from`}} sx={box}/>
                <Typography sx={{fontSize: 11, color: MUTED}}>to</Typography>
                <InputBase type={kind} value={to} onChange={e => onChange("to", e.target.value)}
                           inputProps={{"aria-label": `${facet.Label} to`}} sx={box}/>
            </Box>
            <Typography sx={{fontSize: 10, color: MUTED, pt: "1px"}}>
                {bound(facet, "Min")} – {bound(facet, "Max")}
                {facet.Answered !== undefined ? ` · ${fmt(facet.Answered)} answered` : ""}
            </Typography>
        </Box>
    );
}

/** A question's name, above its answers or its two bounds.
 *
 *  NOT A SECTION HEADER. The four sections above -- Status, Type, Attributes, Other -- are the
 *  pane's own structure and are headed as such; a form's questions all sit INSIDE one of them, so
 *  giving each the same bold rule made every question look like a new part of the pane and the
 *  pane look like it had fifteen sections. Lighter, and the section rule is what separates. */
export function FacetLabel({text}: {text: string}) {
    return (
        <Typography sx={{fontSize: 11, fontWeight: 400, color: TEXT, mt: 1.25, mb: 0.25}}>
            {text}
        </Typography>
    );
}

export function SectionHeader({text}: {text: string}) {
    return (
        <Typography sx={{fontSize: 12, fontWeight: 700, color: HEADING, mt: 2.5, mb: 1, borderBottom: `1px solid ${BORDER}`, pb: 0.5}}>
            {text}
        </Typography>
    );
}

export default function ObjectFilterSidebar({
    selectedStatuses, onToggleStatus,
    statuses = OBJECT_STATUSES,
    statusHeading = "Status",
    selectedTypes, onToggleType, typeHeading = "Type",
    facets, facetsAvailable, facetsTruncated, selectedFacets, onToggleFacet,
    facetRanges, onFacetRangeChange,
    showRecency = true,
    storageKey = "ObjectFilterSidebar",
    newestOnly, onToggleNewest,
    daysOldEnabled, onToggleDaysOld, daysOld, onDaysOldChange,
    statistics, onClear,
}: Props) {
    const countFor = (status: string): number | undefined => {
        if (!statistics) return undefined;
        return statistics.statuses.find(s => s.status === status)?.count ?? 0;
    };

    // 240 IS A DEFAULT NOW, NOT A FIXED WIDTH. It was chosen against the labels this app ships, and
    // the pane also lists object classifications the customer names -- whose length the product does
    // not choose. Shared with the record rail so the two panels behave identically rather than
    // similarly; same control, same idiom, same persistence.
    const {width, collapsed, setCollapsed, panelRef, onGripperDown} = useResizablePanel({
        storageKey, defaultWidth: 240,
        minWidth: 160, maxWidth: 460, collapseAt: 120,
    });

    // The types the account actually defines for this classification. Absent statistics means the
    // counts have not arrived yet, not that there are no types -- the section simply appears with
    // them rather than flashing an empty heading first.
    const types = statistics?.types ?? [];

    const anySelected = selectedStatuses.size > 0
        || (selectedTypes?.size ?? 0) > 0
        || Object.values(selectedFacets ?? {}).some(set => set.size > 0)
        || Object.values(facetRanges ?? {}).some(one => (one?.from ?? "") !== "" || (one?.to ?? "") !== "")
        || (showRecency && (newestOnly || daysOldEnabled));

    return (
        <Box ref={panelRef} sx={{display: "flex", flexDirection: "row", flexShrink: 0, minHeight: 0}}>
        {!collapsed && (
        <Box sx={{width, flexShrink: 0, px: 2.5, py: 2.5, borderRight: `1px solid ${BORDER}`, overflowY: "auto", display: {xs: "none", md: "block"}}}>
            <Box sx={{display: "flex", alignItems: "baseline", justifyContent: "space-between"}}>
                <Typography sx={{fontSize: 11, fontWeight: 700, color: MUTED, letterSpacing: "0.5px", textTransform: "uppercase"}}>
                    Filter By
                </Typography>
                {/* Only offered when something is actually filtered -- a permanently visible "Clear"
                    that does nothing is noise. */}
                {anySelected && (
                    <Typography onClick={onClear}
                                sx={{fontSize: 11, color: LINK, cursor: "pointer", "&:hover": {textDecoration: "underline"}}}>
                        Clear
                    </Typography>
                )}
            </Box>

            {/* Every lifecycle status reads as one list. The terminal three used to sit under a
                separate "Closed" header, which made them look like a different kind of filter than
                the live ones when they are the same filter over the same column. */}
            <SectionHeader text={statusHeading}/>
            {statuses.map(s => (
                <FilterRow key={s.status}
                           label={s.label}
                           count={countFor(s.status)}
                           checked={selectedStatuses.has(s.status)}
                           onToggle={() => onToggleStatus(s.status)}/>
            ))}

            {/* THE TYPE SECTION IS DRIVEN BY THE DATA, not by a flag the caller passes. A
                classification with no types renders nothing here, which is why the change order
                list needed no change to keep looking exactly as it did.

                FILTERED BY ACRONYM, not by USID: the acronym is what the server already accepts as
                a query parameter, what the object number is built from, and what a person says out
                loud. A USID would be the same filter expressed in a word nobody can read. */}
            {onToggleType && types.length > 0 && (
                <>
                    <SectionHeader text={typeHeading}/>
                    {/* DRAWN AS THE TREE IT IS. An object type may specialise another, and ticking
                        a parent filters by everything below it -- so showing the taxonomy flat
                        would make "Screw" look like a peer of "Self-Tapping Screw" and its
                        apparent count a contradiction. Indented by depth, children under parents.

                        A FLAT LIST IS STILL A TREE OF DEPTH ONE, so a classification whose types
                        have no parents renders exactly as it did. */}
                    {nestTypes(types).map(({type, depth}) => (
                        <FilterRow key={type.acronym}
                                   label={type.acronym}
                                   title={type.name || undefined}
                                   count={type.count}
                                   indent={depth}
                                   checked={selectedTypes?.has(type.acronym) ?? false}
                                   onToggle={() => onToggleType(type.acronym)}/>
                    ))}
                </>
            )}

            {/* THE FORM'S OWN QUESTIONS, each under its own heading, because each is a separate
                question rather than another value of one. A facet with no answers is not drawn: an
                empty heading says a question exists and tells the reader nothing they can do.

                COUNTED UNDER THE SAME PERSONA AS THE GRID. The server builds these with the list's
                own visibility predicate, so a number here can never promise a record the reader
                cannot open.

                SAID WHEN CUT, not quietly shortened. A pane showing fifty of two hundred answers
                with no sign of it is worse than one that admits the list is long. */}
            {/* ONE HEADING OVER ALL OF THEM. Every row below is a question some form asks, which
                is one kind of thing and belongs under one heading -- unlike Status and Type, which
                are the record's own columns. Drawn only when there is something under it: a
                heading over nothing says a feature exists and gives the reader nothing to do. */}
            {onToggleFacet && (facets ?? []).some(facet =>
                facet.Kind === "range"
                    ? (facet.Min ?? null) !== null || (facet.Max ?? null) !== null
                    : facet.Values.length > 0) && (
                <>
                    <SectionHeader text="Attributes"/>
                    {/* SAID AT THE SECTION, the way a long answer list is said at the facet. The
                        questions come from the forms in play, and a tenant with twenty bounded
                        questions on one classification gets twelve of them -- each facet is its
                        own grouped query, and a sidebar of twenty expandable sections is a worse
                        way to find a record than no sidebar at all. */}
                    {facetsTruncated ? (
                        <Typography sx={{fontSize: 10, color: MUTED, pl: "19px", pb: "2px"}}>
                            showing {(facets ?? []).length} of {facetsAvailable} questions
                        </Typography>
                    ) : null}
                </>
            )}

            {onToggleFacet && (facets ?? []).map(facet => (
                facet.Kind === "range" ? (
                    (facet.Min ?? null) !== null || (facet.Max ?? null) !== null ? (
                        <React.Fragment key={facet.Code}>
                            <FacetLabel text={facet.Label}/>
                            <RangeRow facet={facet}
                                      from={facetRanges?.[facet.Code]?.from ?? ""}
                                      to={facetRanges?.[facet.Code]?.to ?? ""}
                                      onChange={(bound, value) => onFacetRangeChange?.(facet.Code, bound, value)}/>
                        </React.Fragment>
                    ) : null
                ) : facet.Values.length > 0 ? (
                    <React.Fragment key={facet.Code}>
                        <FacetLabel text={facet.Label}/>
                        {facet.Values.map(answer => (
                            <FilterRow key={`${facet.Code}:${answer.Value}`}
                                       label={answer.Value}
                                       count={answer.Count}
                                       checked={selectedFacets?.[facet.Code]?.has(answer.Value) ?? false}
                                       onToggle={() => onToggleFacet(facet.Code, answer.Value)}/>
                        ))}
                        {facet.Truncated ? (
                            <Typography sx={{fontSize: 10, color: MUTED, pl: "19px", pb: "2px"}}>
                                showing {facet.Values.length} of {facet.DistinctValues}
                            </Typography>
                        ) : null}
                    </React.Fragment>
                ) : null
            ))}

            {/* UNDER THEIR OWN HEADING. Recency filters a different column from everything above it
                -- the creation date, not the lifecycle or the type -- and sitting bare under the
                Status rows made Newest read as a seventh status. "Other" is deliberately vague:
                these two are what is left once the two real axes have their own sections, and
                naming it "Date" would promise a date range this pane does not offer.

                Newest is the fixed one-week window, Days old the arbitrary one, and both are
                uncounted because the counts come from the per-status queries and would not narrow
                with the date. */}
            {showRecency && <SectionHeader text="Other"/>}
            {showRecency && <FilterRow label="Newest" checked={newestOnly} onToggle={onToggleNewest}/>}

            {showRecency && (
            <Box sx={{display: "flex", alignItems: "center", gap: 0.75, py: "2px"}}>
                <Box onClick={onToggleDaysOld} sx={{cursor: "pointer", display: "flex"}}>
                    <Checkbox checked={daysOldEnabled}/>
                </Box>
                {/* Typing in the box turns the filter on: reaching for the number and then having to
                    tick the box as well reads as the control ignoring you. */}
                <InputBase type="number"
                           value={daysOld}
                           onChange={e => onDaysOldChange(e.target.value)}
                           onFocus={() => !daysOldEnabled && onToggleDaysOld()}
                           inputProps={{min: 1}}
                           sx={{
                               width: 44, fontSize: 11, border: `1px solid ${BORDER}`, borderRadius: "2px",
                               "& input": {p: "1px 4px", textAlign: "center"},
                           }}/>
                <Typography onClick={onToggleDaysOld}
                            sx={{fontSize: 11, cursor: "pointer", color: daysOldEnabled ? LINK : TEXT, fontWeight: daysOldEnabled ? 700 : 400}}>
                    Days old
                </Typography>
            </Box>
            )}
        </Box>
        )}

        {/* THE DOT IS NOT DECORATION. A panel dragged shut while filters are active leaves a list
            showing a subset with nothing on screen saying so -- the reader concludes there are five
            objects, not that they filtered to five. The handle carries the fact. */}
        <ResizeGripper collapsed={collapsed} onMouseDown={onGripperDown} setCollapsed={setCollapsed}
                       openLabel={anySelected ? "Open filters (filters are active)" : "Open filters"}
                       marker={collapsed && anySelected}/>
        </Box>
    );
}
